// frontend/src/components/Layout/Header.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LogOut, TrendingUp, TrendingDown } from 'lucide-react';
import { POPULAR_SYMBOLS } from '../../utils/constants';

// Yahoo tickers only ever use these characters (letters/digits plus dot, hyphen,
// caret for indices like ^GSPC, and equals for futures like GC=F). Stripping
// anything else here is the single choke point that keeps stray characters
// (typos, bad pastes) out of every downstream API/WebSocket call.
const sanitizeSymbolInput = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9.\-^=]/g, '');

const MOCK_SYMBOLS = [
  { symbol: 'AAPL', name: 'Apple Inc.', shortName: 'Apple' },
  { symbol: 'MSFT', name: 'Microsoft Corporation', shortName: 'Microsoft' },
  { symbol: 'NVDA', name: 'NVIDIA Corporation', shortName: 'NVIDIA' },
  { symbol: 'AMZN', name: 'Amazon.com, Inc.', shortName: 'Amazon' },
  { symbol: 'META', name: 'Meta Platforms, Inc.', shortName: 'Meta' },
  { symbol: 'TSLA', name: 'Tesla, Inc.', shortName: 'Tesla' },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', shortName: 'Alphabet' },
  { symbol: 'BRK-B', name: 'Berkshire Hathaway Inc.', shortName: 'Berkshire' },
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.', shortName: 'JPMorgan' },
  { symbol: 'V', name: 'Visa Inc.', shortName: 'Visa' },
];

const Header = ({
  symbol,
  setSymbol,
  currentPrice,
  priceChange,
  ma50Rating,
  delayMinutes,
  isLive,
  onToggleLive,
  lastLiveUpdate,
  user,
  onLogout,
  onRequestLogin
}) => {
  const [showQr, setShowQr] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allSymbols, setAllSymbols] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [symbolsLoaded, setSymbolsLoaded] = useState(false);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const SYMBOL_CACHE_KEY = 'yh_symbols_v1';
  const inputRef = useRef(null);
  const displaySymbols = useMemo(() => {
    if (allSymbols.length > 0) return allSymbols;
    return MOCK_SYMBOLS;
  }, [allSymbols]);

  const loadSymbolsOnce = React.useCallback(() => {
    if (symbolsLoaded || loadingSymbols) return;
    setLoadingSymbols(true);

    // Try cache first
    try {
      const cached = localStorage.getItem(SYMBOL_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setAllSymbols(parsed);
          setSymbolsLoaded(true);
          setLoadingSymbols(false);
          return;
        }
      }
    } catch (_) {
      // ignore cache issues
    }

    let mounted = true;
    fetch('/yh_symbols.json')
      .then((res) => res.json())
      .then((data) => {
        if (!mounted) return;
        if (Array.isArray(data)) {
          const normalized = data
            .map((item) => {
              if (typeof item === 'string') {
                return { symbol: item, name: '', shortName: '' };
              }
              if (item && typeof item === 'object' && item.symbol) {
                const name = item.name ? String(item.name) : '';
                const shortName = item.shortName ? String(item.shortName) : '';
                return {
                  symbol: String(item.symbol).toUpperCase(),
                  name,
                  shortName
                };
              }
              return null;
            })
            .filter(Boolean);
          setAllSymbols(normalized);
          setSymbolsLoaded(true);
          try {
            localStorage.setItem(SYMBOL_CACHE_KEY, JSON.stringify(normalized));
          } catch (_) {
            // ignore storage failures
          }
        }
      })
      .catch(() => {
        // keep fallback list
      })
      .finally(() => setLoadingSymbols(false));

    return () => {
      mounted = false;
    };
  }, [SYMBOL_CACHE_KEY, loadingSymbols, symbolsLoaded]);

  // Prefetch lazily after initial view (5s) if not yet loaded
  useEffect(() => {
    const id = setTimeout(() => {
      loadSymbolsOnce();
    }, 5000);
    return () => clearTimeout(id);
  }, [loadSymbolsOnce]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const upper = trimmed.toUpperCase();
    let results = [];
    const source = displaySymbols;
    if (source.length > 0) {
      const prefixMatches = source.filter((item) => item.symbol.startsWith(upper));
      const containsMatches = source.filter((item) => (
        !item.symbol.startsWith(upper) && item.symbol.includes(upper)
      ));
      const nameMatches = source.filter((item) => (
        item.name && item.name.toLowerCase().includes(trimmed.toLowerCase())
      ));

      const merged = [];
      const seen = new Set();
      for (const item of [...prefixMatches, ...containsMatches, ...nameMatches]) {
        if (!seen.has(item.symbol)) {
          seen.add(item.symbol);
          merged.push(item);
        }
        if (merged.length >= 8) break;
      }
      results = merged;
    } else {
      results = POPULAR_SYMBOLS.filter(item => (
        item.symbol.includes(upper) || item.name.toLowerCase().includes(trimmed.toLowerCase())
      )).slice(0, 8);
    }
    setSuggestions(results);
    setShowSuggestions(results.length > 0);
    setHighlightedIndex(results.length > 0 ? 0 : -1);
  }, [query, allSymbols, displaySymbols]);
  const handleSymbolChange = (value) => {
    const next = sanitizeSymbolInput(value);
    if (!next) return;
    setQuery(next);
    setSymbol(next);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <header className="bg-white shadow-lg transition-colors border-b border-gray-200">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-4 w-full lg:flex-row lg:items-center lg:space-x-6">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex items-center space-x-3 p-0.5 rounded-md focus:outline-none"
              title="Refresh"
            >
              <img
                src="/logo.png"
                alt="TheLeaf logo"
                className="h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 object-contain"
              />
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-500 bg-clip-text text-transparent">
                TheLeaf
              </h1>
            </button>
            
            <div className="flex flex-col gap-4 lg:ml-auto lg:flex-row lg:items-center lg:space-x-8">
              <div className="relative w-full lg:w-64">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(sanitizeSymbolInput(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                        handleSymbolChange(suggestions[highlightedIndex].symbol);
                      } else {
                        handleSymbolChange(query);
                      }
                    } else if (e.key === 'Escape') {
                      setShowSuggestions(false);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (!showSuggestions && suggestions.length > 0) {
                        setShowSuggestions(true);
                        setHighlightedIndex(0);
                      } else if (suggestions.length > 0) {
                        setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
                      }
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      if (suggestions.length > 0) {
                        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
                      }
                    }
                  }}
                  onFocus={() => {
                    loadSymbolsOnce();
                    if (suggestions.length > 0) setShowSuggestions(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 100);
                  }}
                  className="px-3 py-2 rounded-lg bg-gray-100 text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 transition-all w-full"
                  placeholder="Search symbol or company"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 mt-2 w-full max-h-64 overflow-y-auto rounded-xl border border-emerald-100 bg-white/95 backdrop-blur shadow-xl">
                    {suggestions.map((item, index) => (
                      <button
                        type="button"
                        key={`${item.symbol}-${item.name}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSymbolChange(item.symbol);
                        }}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={`w-full text-left px-3 py-2 transition-colors ${
                          index === highlightedIndex ? 'bg-emerald-50' : 'hover:bg-emerald-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-semibold text-gray-900">{item.symbol}</div>
                          {(item.shortName || item.name) && (
                            <div className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                              {item.shortName || item.name.split(' ')[0]}
                            </div>
                          )}
                        </div>
                        {item.name && (
                          <div className="text-xs text-gray-500 truncate">{item.name}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-4">
                {currentPrice !== undefined && currentPrice !== null && (
                  <span className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                    {currentPrice.toFixed(2)}
                  </span>
                )}
                {Number.isFinite(delayMinutes) && delayMinutes > 0 && (
                  <span
                    className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700"
                    title={`Quote is delayed by ${delayMinutes} minute${delayMinutes === 1 ? '' : 's'} (source: exchange data license)`}
                  >
                    Delayed {delayMinutes}m
                  </span>
                )}
                {priceChange !== undefined && (
                  <div className={`flex items-center ${
                    priceChange >= 0 ? 'text-emerald-700' : 'text-red-500'
                  }`}>
                    {priceChange >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    <span className="text-sm font-semibold">
                      {Math.abs(priceChange).toFixed(2)}%
                    </span>
                  </div>
                )}
                {ma50Rating !== undefined && (
                  <div className="flex items-center space-x-2 text-sm font-semibold text-emerald-700">
                    <span className="text-gray-500">Rating</span>
                    <div className="flex items-center tracking-wide">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span
                          key={i}
                          className={i < ma50Rating ? 'text-emerald-600 text-base' : 'text-gray-300 text-base'}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {onToggleLive && (
                  <div className="flex flex-wrap items-center gap-3">
                    {isLive && (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-green-600">LIVE</span>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={onToggleLive}
                      className={`px-2 py-1 text-xs rounded ${
                        isLive
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {isLive ? 'Stop' : 'Start'} Live
                    </button>
                    {lastLiveUpdate && (
                      <span className="text-xs text-gray-500">
                        {lastLiveUpdate.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {user && (
              <div className="hidden items-center gap-2 sm:flex">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name || user.email}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                )}
                <div className="max-w-[160px]">
                  <div className="truncate text-sm font-semibold text-gray-900">
                    {user.name || user.email}
                  </div>
                  <div className="truncate text-xs text-gray-500">{user.email}</div>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowQr(true)}
              className="px-5 py-2 rounded-lg text-base shadow-sm whitespace-nowrap"
              style={{
                backgroundColor: '#e7f8ee',
                color: '#111111',
                fontFamily: "'Dancing Script', cursive"
              }}
            >
              Buy me a leaf
            </button>
            {/* Admin link — cosmetic gate only (backend enforces via ADMIN_EMAILS) */}
            {user && ['duchoapc99@gmail.com', 'dev@local'].includes((user.email || '').toLowerCase()) && (
              <a
                href="#/admin"
                className="inline-flex h-10 items-center rounded-lg border border-gray-200 px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                title="Admin dashboard"
              >
                Admin
              </a>
            )}
            {user ? (
              onLogout && (
                <button
                  type="button"
                  onClick={onLogout}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                  title="Sign out"
                >
                  <LogOut size={18} />
                </button>
              )
            ) : (
              onRequestLogin && (
                <button
                  type="button"
                  onClick={() => onRequestLogin()}
                  className="inline-flex h-10 items-center rounded-lg bg-emerald-600 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
                >
                  Log in
                </button>
              )
            )}
          </div>
        </div>
        {showQr && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowQr(false)}
          >
            <div
              className="bg-white rounded-xl p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <div
                  className="text-lg"
                  style={{ fontFamily: "'Dancing Script', cursive", color: '#1f1f1f' }}
                >
                  Buy me a leaf
                </div>
                <button
                  type="button"
                  onClick={() => setShowQr(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Close
                </button>
              </div>
              <img
                src="/qr.jpeg"
                alt="Buy me a leaf"
                className="h-80 w-80 object-contain"
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
